import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useSearch } from "wouter";
import {
  useCreateRecord, useUpdateRecord, useGetRecord,
  useListStudents, useListClasses, useListCourses, useAiPolishText,
  useGetClassStudents, useGetProfile,
  useListTimetables, useGetTimetable,
  getListRecordsQueryKey, getGetRecordQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNccdPolish, usePersonalNccdAdjustments } from "@workspace/api-client-react";
import { AIFeedback } from "@/components/ai/AIFeedback";
import {
  ArrowLeft, Sparkles, Loader2, Users, Calendar, BookOpen,
  ChevronDown, ChevronUp, Search, Star, Check, Mic, Square,
} from "lucide-react";
import { useSpeechInput } from "@/hooks/use-speech-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutoResizeTextarea } from "@/components/ui/auto-resize-textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorBody, getApiErrorMessage } from "@/lib/api-error";
import {
  NCCD_ADJUSTMENTS, groupByCategory,
} from "@/lib/nccd-adjustments";
import { useRegion } from "@/contexts/RegionContext";
import { DataMinimisationTip } from "@/components/DataMinimisationTip";
import {
  formatStudentName,
  studentSortKey,
  useDefaultStudentDisplayMode,
  type Student as DisplayStudent,
} from "@/lib/studentDisplay";

type RecordType = "nccd" | "contact" | "behaviour" | "general";

// ─── NCCD Adjustment Panel ─────────────────────────────────────────────────

interface AdjustmentPanelProps {
  adjustments: { id: string; label: string; category: string; description?: string }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  personalIds: string[];   // sorted by usage
  aiDetectedIds: string[]; // newly detected by AI
}

function AdjustmentPanel({ adjustments, selectedIds, onToggle, personalIds, aiDetectedIds }: AdjustmentPanelProps) {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

  const adjustmentMap = useMemo(() => new Map(adjustments.map((a) => [a.id, a])), [adjustments]);
  const adjustmentsByCategory = useMemo(() => groupByCategory(adjustments as any), [adjustments]);

  const toggleCategory = (cat: string) =>
    setOpenCategories((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });

  const filteredAdjustments = useMemo(() => {
    if (!search) return null;
    const q = search.toLowerCase();
    return adjustments.filter(
      (a) => a.label.toLowerCase().includes(q) || a.description?.toLowerCase().includes(q)
    );
  }, [search, adjustments]);

  // Most-used personal adjustments (top 6, deduplicated)
  const personalTop = useMemo(
    () => personalIds.slice(0, 6).map((id) => adjustmentMap.get(id)).filter(Boolean),
    [personalIds, adjustmentMap]
  );

  const selectedCount = selectedIds.length;

  return (
    <div className="border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-blue-900">Adjustments Made</span>
          {selectedCount > 0 && (
            <Badge className="bg-blue-600 text-white border-0 text-xs py-0">{selectedCount}</Badge>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-blue-700 hover:underline flex items-center gap-1"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? "Collapse" : "Browse all"}
        </button>
      </div>

      <div className="p-4 space-y-4 bg-white">

        {/* Selected chips */}
        {selectedCount > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedIds.map((id) => {
              const adj = adjustmentMap.get(id);
              if (!adj) return null;
              const isNew = aiDetectedIds.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onToggle(id)}
                  className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border font-medium transition-colors ${
                    isNew
                      ? "bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                      : "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                  }`}
                >
                  {isNew && <Sparkles className="h-3 w-3 text-purple-500" />}
                  {adj.label}
                  <span className="text-gray-400 hover:text-gray-700 ml-0.5">×</span>
                </button>
              );
            })}
          </div>
        )}

        {/* AI detected notice */}
        {aiDetectedIds.length > 0 && (
          <div className="flex items-start gap-2 text-xs text-purple-700 bg-purple-50 rounded-lg px-3 py-2 border border-purple-200">
            <Sparkles className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-purple-500" />
            <span>
              AI detected {aiDetectedIds.length} adjustment{aiDetectedIds.length !== 1 ? "s" : ""} from your notes.
              They have been pre-selected above — uncheck any that don't apply.
            </span>
          </div>
        )}

        {/* Personal most-used */}
        {personalTop.length > 0 && !expanded && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              <Star className="h-3 w-3" />
              Your most used
            </div>
            <div className="flex flex-wrap gap-2">
              {personalTop.map((adj) => {
                const sel = selectedIds.includes(adj!.id);
                return (
                  <button
                    key={adj!.id}
                    type="button"
                    onClick={() => onToggle(adj!.id)}
                    className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      sel
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700"
                    }`}
                  >
                    {sel && <Check className="h-3 w-3" />}
                    {adj!.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Expanded: search + full bank */}
        {expanded && (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search adjustments…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Search results */}
            {filteredAdjustments ? (
              <div className="flex flex-wrap gap-2">
                {filteredAdjustments.map((adj) => {
                  const sel = selectedIds.includes(adj.id);
                  return (
                    <button
                      key={adj.id}
                      type="button"
                      onClick={() => onToggle(adj.id)}
                      title={adj.description}
                      className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        sel
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700"
                      }`}
                    >
                      {sel && <Check className="h-3 w-3" />}
                      {adj.label}
                    </button>
                  );
                })}
                {filteredAdjustments.length === 0 && (
                  <p className="text-sm text-gray-400">No adjustments match "{search}"</p>
                )}
              </div>
            ) : (
              /* Grouped by category */
              <div className="space-y-3">
                {Object.entries(adjustmentsByCategory).map(([cat, adjs]) => {
                  const isOpen = openCategories.has(cat);
                  const catSelected = adjs.filter((a) => selectedIds.includes(a.id)).length;
                  return (
                    <div key={cat} className="border border-gray-100 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <span className="text-xs font-semibold text-gray-700">{cat}</span>
                        <div className="flex items-center gap-2">
                          {catSelected > 0 && (
                            <span className="text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-medium">
                              {catSelected}
                            </span>
                          )}
                          {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                        </div>
                      </button>
                      {isOpen && (
                        <div className="p-3 flex flex-wrap gap-2">
                          {adjs.map((adj) => {
                            const sel = selectedIds.includes(adj.id);
                            return (
                              <button
                                key={adj.id}
                                type="button"
                                onClick={() => onToggle(adj.id)}
                                title={adj.description}
                                className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                  sel
                                    ? "bg-blue-600 border-blue-600 text-white"
                                    : "bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-700"
                                }`}
                              >
                                {sel && <Check className="h-3 w-3" />}
                                {adj.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Empty state when nothing selected and not expanded */}
        {selectedCount === 0 && !expanded && personalTop.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-2">
            Polish with AI to auto-detect adjustments, or browse to add them manually.
          </p>
        )}

        {selectedCount === 0 && !expanded && personalTop.length > 0 && null}
      </div>
    </div>
  );
}

// ─── Main form ──────────────────────────────────────────────────────────────

// Used when timetable data is unavailable (no timetable set up, weekend, loading).
const FALLBACK_PERIOD_OPTIONS: readonly string[] = [
  "Before School",
  "Period 1",
  "Period 2",
  "Period 3",
  "Period 4",
  "Period 5",
  "Period 6",
  "Lunch",
  "Small break",
  "After school",
] as const;

const RECORD_DAY_NAMES = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
] as const;

export default function RecordNew() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const editId = params.get("edit") ? parseInt(params.get("edit")!) : null;
  const returnTo = params.get("from") ?? "/app/records";
  const { toast } = useToast();
  const { terms } = useRegion();

  const RECORD_TYPES: { value: RecordType; label: string; color: string }[] = [
    { value: "general",   label: "General Note",             color: "bg-gray-100 text-gray-700" },
    { value: "nccd",      label: terms.adjustmentType,       color: "bg-blue-100 text-blue-700" },
    { value: "contact",   label: "Contact Note",             color: "bg-green-100 text-green-700" },
    { value: "behaviour", label: `${terms.behaviourSpelling} Note`, color: "bg-orange-100 text-orange-700" },
  ];

  const ctxDate     = params.get("date");
  const ctxPeriod   = params.get("period");
  const ctxSubject  = params.get("subject");
  const ctxCourseId = params.get("courseId") ? parseInt(params.get("courseId")!) : null;
  const ctxClassId  = params.get("classId")  ? parseInt(params.get("classId")!)  : null;
  const ctxStudentIds = params.get("studentIds")
    ? params.get("studentIds")!.split(",").map(Number).filter((n) => !isNaN(n))
    : null;

  const queryClient = useQueryClient();
  const { data: profile }   = useGetProfile();
  const { data: existing } = useGetRecord(editId!, { query: { enabled: !!editId } });
  const { data: students }  = useListStudents();
  const { data: classes }   = useListClasses();
  const { data: courses }   = useListCourses();

  // Fetch teacher's timetable so period options can mirror Diary slot labels.
  const { data: timetables } = useListTimetables();
  const defaultTimetable = ((timetables ?? []) as any[])[0];
  const { data: timetableDetail } = useGetTimetable(
    defaultTimetable?.id ?? 0,
    { query: { enabled: !!defaultTimetable?.id } },
  );

  // Derive preferences from profile
  const profilePrefs = useMemo(() => {
    const p = profile as any;
    return {
      enabledRecordTypes: (p?.preferences?.enabledRecordTypes as string[] | undefined) ?? ["nccd", "behaviour", "contact", "general"],
      defaultRecordType: (p?.preferences?.defaultRecordType as string | undefined) ?? "general",
      customAdjustments: (p?.preferences?.customAdjustments as { id: string; label: string; category: string }[] | undefined) ?? [],
      hiddenAdjustmentIds: (p?.preferences?.hiddenAdjustmentIds as string[] | undefined) ?? [],
      renamedAdjustments: (p?.preferences?.renamedAdjustments as Record<string, string> | undefined) ?? {},
    };
  }, [profile]);

  const effectiveAdjustments = useMemo(() => {
    const standard = NCCD_ADJUSTMENTS
      .filter((a) => !profilePrefs.hiddenAdjustmentIds.includes(a.id))
      .map((a) => ({ ...a, label: profilePrefs.renamedAdjustments[a.id] ?? a.label }));
    return [...standard, ...profilePrefs.customAdjustments];
  }, [profilePrefs]);

  // Filtered RECORD_TYPES based on user preferences
  const ENABLED_RECORD_TYPES = RECORD_TYPES.filter((rt) =>
    profilePrefs.enabledRecordTypes.includes(rt.value)
  );

  const [type,     setType]     = useState<RecordType>("general");
  const [date,     setDate]     = useState(ctxDate ?? format(new Date(), "yyyy-MM-dd"));
  const [period,   setPeriod]   = useState(ctxPeriod ?? "");
  const [subject,  setSubject]  = useState(ctxSubject ?? "");
  const [courseId, setCourseId] = useState<number | null>(ctxCourseId);
  const [classId,  setClassId]  = useState<number | null>(ctxClassId);

  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>(ctxStudentIds ?? []);
  const [studentSearch,      setStudentSearch]      = useState("");
  const [showAllStudents,    setShowAllStudents]    = useState(!ctxClassId);

  const [shorthandNotes,    setShorthandNotes]    = useState("");
  const [description,       setDescription]       = useState("");
  const [polishAiUsageId,   setPolishAiUsageId]   = useState<number | null>(null);
  const [selectedAdjustmentIds, setSelectedAdjustmentIds] = useState<string[]>([]);
  const [aiDetectedIds,     setAiDetectedIds]     = useState<string[]>([]);
  const [enteredIntoSentral, setEnteredIntoSentral] = useState(false);

  // Build period dropdown options from the teacher's timetable for the selected
  // date's day-of-week. Collects labels across ALL weeks (deduped, position-ordered)
  // so a rotating fortnightly timetable shows every block that can appear on that
  // day. Falls back to FALLBACK_PERIOD_OPTIONS when no timetable data is available
  // (initial load, no timetable set up, weekend).
  const periodOptions = useMemo((): string[] => {
    const weeks: any[] = (timetableDetail as any)?.weeks ?? [];
    if (weeks.length === 0) return [...FALLBACK_PERIOD_OPTIONS];
    const dayOfWeek = RECORD_DAY_NAMES[new Date(date + "T12:00:00").getDay()];
    const seen = new Set<string>();
    const labels: string[] = [];
    for (const week of weeks) {
      const daySlots: any[] = (week.slots ?? [])
        .filter((s: any) => s.day === dayOfWeek)
        .sort((a: any, b: any) => a.position - b.position);
      for (const slot of daySlots) {
        if (slot.label && !seen.has(slot.label)) {
          seen.add(slot.label);
          labels.push(slot.label);
        }
      }
    }
    return labels.length > 0 ? labels : [...FALLBACK_PERIOD_OPTIONS];
  }, [timetableDetail, date]);

  const { data: classStudents } = useGetClassStudents(classId ?? 0, {
    query: { enabled: !!classId },
  });
  const { data: personalBank } = usePersonalNccdAdjustments();

  const createRecord = useCreateRecord();
  const updateRecord = useUpdateRecord();
  const polishText   = useAiPolishText();
  const nccdPolish   = useNccdPolish();

  // Set default record type from profile preferences (new records only)
  useEffect(() => {
    if (!editId && profile) {
      const p = profile as any;
      const defaultType = p.preferences?.defaultRecordType as RecordType | undefined;
      if (defaultType) setType(defaultType);
    }
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load existing record when editing
  useEffect(() => {
    if (existing) {
      setType(existing.type as RecordType);
      setDate(existing.date);
      setPeriod(existing.period ?? "");
      setSubject(existing.subject ?? "");
      setCourseId(existing.courseId ?? null);
      setClassId(existing.classId ?? null);
      setSelectedStudentIds(existing.studentIds);
      setShorthandNotes(existing.shorthandNotes ?? "");
      setDescription(existing.description ?? "");
      setShowAllStudents(!existing.classId);
      setEnteredIntoSentral((existing as any).enteredIntoSentral ?? false);
      const fields = existing.fields as { adjustments?: string[] } | null;
      if (fields?.adjustments) setSelectedAdjustmentIds(fields.adjustments);
    }
  }, [existing]);

  const handlePolish = async () => {
    if (!shorthandNotes.trim()) return;
    const student = selectedStudentIds.length === 1
      ? students?.find((s) => s.id === selectedStudentIds[0])
      : undefined;
    // Task #158 — never send a real student name to the server. The
    // server looks up the display name from the studentId as a defence
    // layer before placeholder-substituting it for the AI provider.
    const studentId = student?.id;

    try {
      if (type === "nccd") {
        // Use NCCD-specific polish that also detects adjustments
        const effectiveIds = effectiveAdjustments.map((a) => a.id);
        const result = await nccdPolish.mutateAsync({
          text: shorthandNotes,
          studentId,
          adjustmentIds: effectiveIds,
        });
        setDescription(result.polished);
        setPolishAiUsageId(result.aiUsageId ?? null);

        // Merge AI-detected adjustments with any already selected
        const newDetected = result.adjustments.filter((id) => effectiveIds.includes(id));
        setAiDetectedIds(newDetected);
        setSelectedAdjustmentIds((prev) => {
          const merged = new Set([...prev, ...newDetected]);
          return [...merged];
        });

        toast({
          title: "Notes polished!",
          description: newDetected.length > 0
            ? `${newDetected.length} adjustment${newDetected.length !== 1 ? "s" : ""} detected from your notes.`
            : "Rewritten as a professional NCCD record.",
        });
      } else {
        const result = await polishText.mutateAsync({
          data: { text: shorthandNotes, recordType: type as any, studentId },
        });
        setDescription(result.polished);
        setPolishAiUsageId(result.aiUsageId ?? null);
        toast({ title: "Notes polished!", description: "Rewritten as a professional record." });
      }
    } catch (err) {
      const body = getApiErrorBody(err);
      // The /ai/* routes still signal these via boolean flags
      // (`subscriptionExpired` / `upgradeRequired`); accept the
      // forward-compat `errorCode` form too in case the server moves to
      // it later.
      const expired =
        body?.subscriptionExpired === true ||
        body?.errorCode === "subscription_expired";
      const upgrade =
        body?.upgradeRequired === true ||
        body?.errorCode === "upgrade_required";
      if (expired) {
        toast({
          title: "Subscribe to use AI polish",
          description:
            "Your trial has ended. Subscribe to keep using AI polish — your existing notes are safe.",
          variant: "destructive",
        });
      } else if (upgrade) {
        toast({
          title: "AI polish unavailable",
          description: body?.error ?? "Subscribe to keep using AI polish.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "AI polish failed",
          description: getApiErrorMessage(err, "Please try again in a moment."),
          variant: "destructive",
        });
      }
    }
  };

  const toggleAdjustment = (id: string) =>
    setSelectedAdjustmentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const toggleStudent = (id: number) =>
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const studentPool = showAllStudents
    ? (students ?? [])
    : (classStudents ?? students ?? []);

  const accountDisplayMode = useDefaultStudentDisplayMode();
  const filteredStudents = studentPool
    .filter((s) => {
      // Search across every identifier so a teacher can still type a
      // surname even when the UI is showing first names or initials.
      const haystack = [
        s.firstName,
        (s as any).lastName,
        (s as any).preferredName,
        (s as any).studentCode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(studentSearch.toLowerCase());
    })
    .slice()
    .sort((a, b) => studentSortKey(a as DisplayStudent).localeCompare(studentSortKey(b as DisplayStudent)));

  const selectedClass  = classes?.find((c) => c.id === classId);
  const selectedCourse = courses?.find((c) => c.id === courseId);

  // Personal bank: sorted ids
  const personalIds = useMemo(
    () => (personalBank?.adjustments ?? []).map((a) => a.id),
    [personalBank]
  );

  const isPolishing = polishText.isPending || nccdPolish.isPending;

  const handleSpeechResult = useCallback((transcript: string) => {
    setShorthandNotes((prev) => prev ? `${prev} ${transcript}` : transcript);
  }, []);

  const speech = useSpeechInput({ onResult: handleSpeechResult });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fields = type === "nccd" && selectedAdjustmentIds.length > 0
      ? { adjustments: selectedAdjustmentIds }
      : undefined;

    const data = {
      type: type as any,
      // Schema requires `status` (enum). The form has no status picker,
      // so default new records to "active".
      status: "active" as const,
      date,
      period:           period    || null,
      teacherName:      null,
      location:         null,
      subject:          subject   || null,
      isConfidential:     false,
      linkedToActivity:   false,
      enteredIntoSentral: enteredIntoSentral,
      studentIds:         selectedStudentIds,
      shorthandNotes:     shorthandNotes || null,
      description:        description   || null,
      courseId:           courseId ?? null,
      classId:            classId  ?? null,
      fields,
    };
    try {
      if (editId) {
        await updateRecord.mutateAsync({ recordId: editId, data });
        queryClient.invalidateQueries({ queryKey: getListRecordsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetRecordQueryKey(editId) });
        toast({ title: "Record updated" });
      } else {
        await createRecord.mutateAsync({ data });
        queryClient.invalidateQueries({ queryKey: getListRecordsQueryKey() });
        toast({ title: "Record saved" });
      }
      setLocation(returnTo);
    } catch (err) {
      toast({
        title: editId ? "Couldn't update the record" : "Couldn't save the record",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const currentType = RECORD_TYPES.find((t) => t.value === type)!;

  return (
    <div className="p-6 max-w-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setLocation(returnTo)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold text-gray-900 flex-1">
          {editId ? "Edit Record" : "New Record"}
        </h1>
      </div>

      {/* Record Details card */}
      <div className="bg-white border rounded-xl p-5 mb-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 text-base">Record details</h2>
          <Select value={type} onValueChange={(v) => { setType(v as RecordType); setAiDetectedIds([]); setSelectedAdjustmentIds([]); }}>
            <SelectTrigger className={`h-7 text-xs border-0 shadow-none px-2.5 rounded-full font-medium ${currentType.color}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENABLED_RECORD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* Date */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Date</Label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Period */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Period</Label>
            <Select
              value={period || "__none__"}
              onValueChange={(v) => setPeriod(v === "__none__" ? "" : v)}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="No period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="text-gray-400">No period</span>
                </SelectItem>
                {/* Preserve existing free-text period values gracefully */}
                {period && !periodOptions.includes(period) && (
                  <SelectItem value={period}>
                    {period}{" "}
                    <span className="text-xs text-gray-400">(existing)</span>
                  </SelectItem>
                )}
                {periodOptions.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Class */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Class</Label>
            <Select
              value={classId ? String(classId) : "none"}
              onValueChange={(v) => {
                const id = v === "none" ? null : Number(v);
                setClassId(id);
                setCourseId(null);
                setShowAllStudents(!id);
                setSelectedStudentIds([]);
              }}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select class…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none"><span className="text-gray-400">No class</span></SelectItem>
                {(classes ?? []).map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject / Course */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Subject</Label>
            <Select
              value={courseId ? String(courseId) : "none"}
              onValueChange={(v) => {
                const id = v === "none" ? null : Number(v);
                setCourseId(id);
                const found = courses?.find((c) => c.id === id);
                setSubject(found?.name ?? "");
              }}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Select subject…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none"><span className="text-gray-400">No subject</span></SelectItem>
                {(courses ?? []).map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Students Involved */}
        <div className="bg-white border rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 text-base">Students Involved</h2>
            {classId && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Users className="h-3.5 w-3.5" />
                <button type="button" className={`underline ${!showAllStudents ? "text-primary font-semibold" : ""}`} onClick={() => setShowAllStudents(false)}>
                  {selectedClass?.name ?? "Class"} only
                </button>
                <span>·</span>
                <button type="button" className={`underline ${showAllStudents ? "text-primary font-semibold" : ""}`} onClick={() => setShowAllStudents(true)}>
                  All students
                </button>
              </div>
            )}
          </div>

          {selectedStudentIds.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedStudentIds.map((id) => {
                const s = students?.find((x) => x.id === id);
                return (
                  <Badge key={id} variant="secondary" className="cursor-pointer gap-1" onClick={() => toggleStudent(id)}>
                    {s ? formatStudentName(s as DisplayStudent, accountDisplayMode) : "Hidden student"}
                    <span className="text-gray-400 hover:text-gray-900">×</span>
                  </Badge>
                );
              })}
            </div>
          )}

          {classId && classStudents && classStudents.length > 0 && !showAllStudents && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedStudentIds(classStudents.map((s: any) => s.id))}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border font-medium transition-colors flex-1 ${
                  selectedStudentIds.length === classStudents.length &&
                  (classStudents as any[]).every((s: any) => selectedStudentIds.includes(s.id))
                    ? "bg-primary/10 border-primary/30 text-primary"
                    : "bg-white border-gray-200 text-gray-700 hover:border-primary/40 hover:text-primary hover:bg-primary/5"
                }`}
              >
                <Users className="h-4 w-4 flex-shrink-0" />
                Whole class
                <span className="ml-auto text-gray-400 font-normal text-sm">{classStudents.length} students</span>
              </button>
              {selectedStudentIds.length > 0 && (
                <Button type="button" variant="ghost" size="sm" className="h-10 text-xs text-gray-500"
                  onClick={() => setSelectedStudentIds([])}>
                  Clear
                </Button>
              )}
            </div>
          )}

          <Input
            placeholder="Search students…"
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
          />

          <div className="border rounded-lg max-h-52 overflow-y-auto divide-y">
            {filteredStudents.slice(0, 40).map((student) => (
              <label key={student.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50">
                <Checkbox checked={selectedStudentIds.includes(student.id)} onCheckedChange={() => toggleStudent(student.id)} />
                <span className="text-sm text-gray-800 flex-1">
                  {formatStudentName(student as DisplayStudent, accountDisplayMode)}
                  {student.yearLevel && <span className="text-gray-400 ml-2 text-xs">Year {student.yearLevel}</span>}
                </span>
              </label>
            ))}
            {filteredStudents.length === 0 && (
              <p className="px-4 py-6 text-sm text-center text-gray-500">
                {studentSearch ? "No students match your search" : "No students available"}
              </p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border rounded-xl p-6 shadow-sm space-y-4">
          <h2 className="font-semibold text-gray-900 text-base">Notes</h2>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Quick notes</Label>
              {speech.supported && (
                speech.listening ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-red-500 animate-pulse">Listening…</span>
                    <button
                      type="button"
                      onClick={speech.toggle}
                      aria-label="Stop dictation"
                      title="Stop dictation"
                      className="h-9 w-9 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors"
                    >
                      <Square className="h-4 w-4 fill-current" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={speech.toggle}
                    aria-label="Start dictation"
                    title="Start dictation"
                    className="h-9 w-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    <Mic className="h-4 w-4" />
                  </button>
                )
              )}
            </div>
            <AutoResizeTextarea
              value={shorthandNotes}
              onChange={(e) => setShorthandNotes(e.target.value)}
              placeholder={
                type === "nccd"
                  ? "e.g. gave Tommy scaffolding sheet, brain break at 10 mins, read instructions aloud, extra time on assessment…"
                  : "Jot your rough notes here… AI can turn them into a formal record."
              }
            />
            {!speech.supported && (
              <p className="text-xs text-gray-400">
                Dictation is not available in this browser. You can still use your phone keyboard microphone.
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handlePolish}
              disabled={isPolishing || !shorthandNotes.trim()}
            >
              {isPolishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-purple-500" />}
              {type === "nccd" ? "Polish & detect adjustments" : "Polish with AI"}
            </Button>
            <DataMinimisationTip className="mt-1.5" />
          </div>

          <div className="space-y-2">
            <Label>Formal description</Label>
            <AutoResizeTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === "nccd"
                  ? "Professional NCCD adjustment record suitable for compliance documentation…"
                  : "Professional record suitable for official documentation…"
              }
            />
            <AIFeedback aiUsageId={polishAiUsageId} />
            <DataMinimisationTip className="mt-1.5" />
          </div>
        </div>

        {/* NCCD Adjustment Panel — only for NCCD records */}
        {type === "nccd" && (
          <AdjustmentPanel
            adjustments={effectiveAdjustments}
            selectedIds={selectedAdjustmentIds}
            onToggle={toggleAdjustment}
            personalIds={personalIds}
            aiDetectedIds={aiDetectedIds}
          />
        )}

        {/* System upload compliance */}
        <div className="bg-white border rounded-xl p-5 shadow-sm">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={enteredIntoSentral}
              onCheckedChange={(v) => setEnteredIntoSentral(!!v)}
              className="mt-0.5"
            />
            <div>
              <p className="font-medium text-gray-900 text-sm">Entered into school system</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Mark this when you've uploaded the record into Sentral or your school's SIMS. Your head teacher can track compliance.
              </p>
            </div>
          </label>
        </div>

        <div className="flex justify-end gap-3 pb-8">
          <Button type="button" variant="outline" onClick={() => setLocation("/app/records")}>
            Cancel
          </Button>
          <Button type="submit" disabled={createRecord.isPending || updateRecord.isPending} data-write-action>
            {(createRecord.isPending || updateRecord.isPending) && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {editId ? "Update Record" : "Save Record"}
          </Button>
        </div>
      </form>
    </div>
  );
}
